// src/pages/RawMaterialPurchases/NewRawMaterialPurchase.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, writeBatch, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { getExchangeRates } from '../../utils/currencyUtils';

// Desteklenen döviz türleri
const CURRENCIES = [
    { code: 'TRY', symbol: '₺', name: 'Türk Lirası' },
    { code: 'USD', symbol: '$', name: 'Amerikan Doları' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'İngiliz Sterlini' }
];

const styles = {
    container: { fontFamily: '"Inter", sans-serif', maxWidth: '1200px', margin: '30px auto', padding: '0 20px' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
    title: { fontSize: '28px', fontWeight: '700', color: '#1e293b', margin: 0 },
    box: { background: 'white', padding: '30px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', border: '1px solid #e2e8f0' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '25px' },
    formGroup: { marginBottom: '20px', position: 'relative' },
    label: { display: 'block', marginBottom: '8px', fontWeight: '600', color: '#64748b', fontSize: '14px' },
    input: { width: '100%', padding: '12px 16px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box' },
    select: { width: '100%', padding: '12px 16px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box', background: 'white', cursor: 'pointer' },
    table: { width: '100%', borderCollapse: 'separate', borderSpacing: '0', marginTop: '20px' },
    th: { background: '#f8fafc', padding: '16px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase' },
    td: { padding: '16px', borderBottom: '1px solid #e2e8f0', color: '#334155' },
    totals: { marginTop: '30px', display: 'flex', justifyContent: 'flex-end' },
    totalBox: { background: '#f8fafc', padding: '20px', borderRadius: '12px', minWidth: '320px', border: '1px solid #e2e8f0' },
    totalRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '15px' },
    grandTotal: { fontWeight: '700', borderTop: '2px solid #cbd5e1', paddingTop: '15px', marginTop: '10px', fontSize: '18px', color: '#0f172a' },
    button: { padding: '12px 24px', border: 'none', background: 'linear-gradient(135deg, #06C167, #8b5cf6)', color: 'white', borderRadius: '10px', cursor: 'pointer', fontSize: '15px', fontWeight: '600', transition: 'all 0.2s' },
    secondaryButton: { padding: '12px 24px', border: '1px solid #cbd5e1', background: 'white', color: '#475569', borderRadius: '10px', cursor: 'pointer', fontSize: '15px', fontWeight: '600', marginRight: '10px' },
    suggestions: { position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto', zIndex: 50, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' },
    suggestionItem: { padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#334155', transition: 'background 0.1s' },
    deleteButton: { background: '#fee2e2', border: 'none', color: '#ef4444', cursor: 'pointer', borderRadius: '6px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' },
    itemInputRow: { display: 'grid', gridTemplateColumns: 'minmax(200px, 2fr) 80px 100px 100px auto', gap: '15px', alignItems: 'end', background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px dashed #cbd5e1', marginBottom: '20px' },
    currencyBadge: { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' },
    exchangeRateBox: { background: 'linear-gradient(135deg, #fef3c7, #fef9c3)', padding: '16px 20px', borderRadius: '12px', border: '1px solid #fcd34d', marginBottom: '20px' },
    tcmbBadge: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#dcfce7', color: '#166534', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' },
    refreshButton: { background: 'none', border: 'none', color: '#06C167', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '4px' }
};

function NewRawMaterialPurchase() {
    const { userData } = useAuth();
    const navigate = useNavigate();

    const [suppliers, setSuppliers] = useState([]);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [productGroups, setProductGroups] = useState([]);
    const [companyInfo, setCompanyInfo] = useState(null);

    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [supplierNameInput, setSupplierNameInput] = useState('');
    const [showSupplierSuggestions, setShowSupplierSuggestions] = useState(false);

    const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split('T')[0]);
    const [lineItems, setLineItems] = useState([]);

    // Döviz ve Kur State'leri
    const [currency, setCurrency] = useState('TRY');
    const [exchangeRate, setExchangeRate] = useState(1);
    const [tcmbRates, setTcmbRates] = useState(null);
    const [loadingRates, setLoadingRates] = useState(false);
    const [rateSource, setRateSource] = useState('manual'); // 'tcmb' veya 'manual'

    const [currentItem, setCurrentItem] = useState({ productId: '', productName: '', quantity: '', price: '', unit: 'Adet' });
    const [showMaterialSuggestions, setShowMaterialSuggestions] = useState(false);
    const [note, setNote] = useState('');

    // Döviz sembolünü getir
    const getCurrencySymbol = (code) => CURRENCIES.find(c => c.code === code)?.symbol || '₺';

    // TCMB'den kurları çek
    const fetchRates = async () => {
        setLoadingRates(true);
        try {
            const rates = await getExchangeRates();
            if (rates.success) {
                setTcmbRates(rates);
                // Eğer mevcut döviz TRY değilse, otomatik kuru ayarla
                if (currency !== 'TRY' && rates[currency]) {
                    setExchangeRate(rates[currency]);
                    setRateSource('tcmb');
                }
            }
        } catch (error) {
            console.error('Kur çekme hatası:', error);
        }
        setLoadingRates(false);
    };

    // Sayfa yüklendiğinde kurları çek
    useEffect(() => {
        fetchRates();
    }, []);

    useEffect(() => {
        if (!userData?.companyId) return;
        const companyId = userData.companyId;

        const fetchCompanyInfo = async () => {
            const companyDocRef = doc(db, 'companies', companyId);
            const docSnap = await getDoc(companyDocRef);
            if (docSnap.exists()) setCompanyInfo(docSnap.data());
        };
        fetchCompanyInfo();

        const unsubSuppliers = onSnapshot(query(collection(db, 'companies', companyId, 'suppliers')), snap => setSuppliers(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubProducts = onSnapshot(query(collection(db, 'companies', companyId, 'products')), snap => setRawMaterials(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.isRawMaterial)));
        const unsubGroups = onSnapshot(query(collection(db, 'companies', companyId, 'productGroups')), snap => setProductGroups(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

        return () => { unsubSuppliers(); unsubProducts(); unsubGroups(); };
    }, [userData]);

    const handleSupplierSelect = (supplier) => {
        setSupplierNameInput(supplier.name);
        setSelectedSupplier(supplier);
        setShowSupplierSuggestions(false);
    };

    const handleRawMaterialSelect = (rawMaterial) => {
        const group = productGroups.find(g => g.id === rawMaterial.groupId);
        let price = '';
        // If product has a specific purchase price, use it. Otherwise fall back to group default.
        if (rawMaterial.purchasePrice) {
            price = rawMaterial.purchasePrice;
        } else if (group && group.defaultPurchasePrice && group.defaultPurchasePrice.currency === 'TRY') {
            price = group.defaultPurchasePrice.amount || 0;
        }

        setCurrentItem({
            productId: rawMaterial.id,
            productName: rawMaterial.name,
            quantity: 1,
            price: price,
            unit: rawMaterial.unit || 'Adet'
        });
        setShowMaterialSuggestions(false);
    };

    const handleAddItem = () => {
        if (!currentItem.productId || !currentItem.quantity || !currentItem.price) {
            alert("Lütfen tüm alanları doldurun.");
            return;
        }
        setLineItems(prev => [...prev, {
            ...currentItem,
            quantity: Number(currentItem.quantity),
            price: Number(currentItem.price),
            total: Number(currentItem.quantity) * Number(currentItem.price)
        }]);
        setCurrentItem({ productId: '', productName: '', quantity: '', price: '', unit: 'Adet' });
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...lineItems];
        const item = newItems[index];
        item[field] = Number(value);
        item.total = item.quantity * item.price;
        setLineItems(newItems);
    };

    const handleRemoveItem = (index) => {
        const newItems = [...lineItems];
        newItems.splice(index, 1);
        setLineItems(newItems);
    };

    const total = lineItems.reduce((acc, item) => acc + item.total, 0);
    const totalTRY = total * exchangeRate; // TL karşılığı

    const handleSave = async () => {
        if (!selectedSupplier || lineItems.length === 0) {
            alert("Lütfen bir tedarikçi seçin ve en az bir hammadde ekleyin.");
            return;
        }

        // TRY dışı döviz için kur kontrolü
        if (currency !== 'TRY' && (!exchangeRate || exchangeRate <= 0)) {
            alert("Lütfen geçerli bir döviz kuru girin.");
            return;
        }

        const batch = writeBatch(db);
        const companyId = userData.companyId;

        // Create the raw material purchase record
        const purchaseRef = doc(collection(db, 'companies', companyId, 'rawMaterialPurchases'));
        const purchaseData = {
            supplierId: selectedSupplier.id,
            supplierName: selectedSupplier.name,
            purchaseDate: new Date(purchaseDate),
            note: note,
            lineItems: lineItems.map(item => ({
                ...item,
                name: item.productName,
                priceTRY: item.price * exchangeRate // Her kalemin TL karşılığı
            })),
            total,
            currency,
            exchangeRate: Number(exchangeRate),
            totalTRY,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        };
        batch.set(purchaseRef, purchaseData);

        // Create invoice
        const invoiceRef = doc(collection(db, 'companies', companyId, 'purchaseInvoices'));
        const invoiceData = {
            supplierId: selectedSupplier.id,
            supplierName: selectedSupplier.name,
            invoiceDate: new Date(purchaseDate),
            note: note,
            lineItems: lineItems.map(({ productName, unit, ...rest }) => ({
                ...rest,
                name: productName,
                priceTRY: rest.price * exchangeRate
            })),
            total,
            currency,
            exchangeRate: Number(exchangeRate),
            totalTRY,
            isFromRawMaterialPurchase: true,
            rawMaterialPurchaseId: purchaseRef.id
        };
        batch.set(invoiceRef, invoiceData);

        // Update product quantities AND PURCHASE PRICE (TL olarak kaydediyoruz)
        for (const item of lineItems) {
            const productRef = doc(db, 'companies', companyId, 'products', item.productId);
            const movementRef = doc(collection(productRef, 'movements'));
            const productDoc = await getDoc(productRef);

            if (productDoc.exists()) {
                const productData = productDoc.data();
                const newQuantity = (productData.quantity || 0) + Number(item.quantity);
                const priceTRY = Number(item.price) * exchangeRate;

                // CRITICAL: Update purchasePrice to reflect the latest cost (in TRY)
                batch.update(productRef, {
                    quantity: newQuantity,
                    purchasePrice: priceTRY // TL olarak kaydediyoruz
                });

                batch.set(movementRef, {
                    date: new Date(),
                    type: 'Hammadde Satın Alma',
                    quantity: `+${item.quantity} ${item.unit}`,
                    description: `Satın Alma: ${purchaseRef.id.substring(0, 8)} (${currency})`,
                    price: priceTRY,
                    originalPrice: Number(item.price),
                    currency,
                    exchangeRate: Number(exchangeRate)
                });
            }
        }

        // Update supplier balance (TL olarak)
        const supplierRef = doc(db, 'companies', companyId, 'suppliers', selectedSupplier.id);
        const newBalance = (selectedSupplier.balance || 0) + totalTRY;
        batch.update(supplierRef, { balance: newBalance });

        try {
            await batch.commit();
            alert('Satın alma başarıyla kaydedildi! Hammadde maliyetleri güncellendi.');
            navigate('/hammadde-satin-alma');
        } catch (error) {
            console.error("Hata:", error);
            alert('Hata: ' + error.message);
        }
    };

    const filteredSuppliers = supplierNameInput ? suppliers.filter(s => s.name.toLowerCase().includes(supplierNameInput.toLowerCase())) : [];
    const filteredRawMaterials = currentItem.productName ? rawMaterials.filter(p => p.name.toLowerCase().includes(currentItem.productName.toLowerCase())) : [];

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Yeni Hammadde Satın Alma</h1>
                <button onClick={() => navigate('/hammadde-satin-alma')} style={styles.secondaryButton}>Vazgeç</button>
            </div>

            <div style={styles.box}>
                <div style={styles.grid}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Tedarikçi Firma *</label>
                        <input
                            type="text"
                            placeholder="Tedarikçi aramaya başlayın..."
                            style={styles.input}
                            value={supplierNameInput}
                            onChange={e => {
                                setSupplierNameInput(e.target.value);
                                setSelectedSupplier(null);
                                setShowSupplierSuggestions(true);
                            }}
                            onFocus={() => setShowSupplierSuggestions(true)}
                        />
                        {showSupplierSuggestions && filteredSuppliers.length > 0 && !selectedSupplier && (
                            <div style={styles.suggestions}>
                                {filteredSuppliers.map(s => (
                                    <div
                                        key={s.id}
                                        style={styles.suggestionItem}
                                        onClick={() => handleSupplierSelect(s)}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                    >
                                        <div style={{ fontWeight: '500' }}>{s.name}</div>
                                        <div style={{ fontSize: '12px', color: '#94a3b8' }}>{s.contactPerson}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>İşlem Tarihi</label>
                        <input style={styles.input} type="date" value={purchaseDate} onChange={e => setPurchaseDate(e.target.value)} />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Döviz Türü</label>
                        <select
                            style={styles.select}
                            value={currency}
                            onChange={e => {
                                const newCurrency = e.target.value;
                                setCurrency(newCurrency);
                                if (newCurrency === 'TRY') {
                                    setExchangeRate(1);
                                    setRateSource('manual');
                                } else if (tcmbRates && tcmbRates[newCurrency]) {
                                    // TCMB kurunu otomatik uygula
                                    setExchangeRate(tcmbRates[newCurrency]);
                                    setRateSource('tcmb');
                                } else {
                                    setRateSource('manual');
                                }
                            }}
                        >
                            {CURRENCIES.map(c => (
                                <option key={c.code} value={c.code}>
                                    {c.symbol} {c.name}
                                    {c.code !== 'TRY' && tcmbRates && tcmbRates[c.code] && ` (${tcmbRates[c.code].toFixed(4)} ₺)`}
                                </option>
                            ))}
                        </select>
                    </div>
                    {currency !== 'TRY' && (
                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Döviz Kuru (1 {currency} = ? TL)
                                {rateSource === 'tcmb' && (
                                    <span style={{ ...styles.tcmbBadge, marginLeft: '8px' }}>
                                        🏛️ TCMB
                                    </span>
                                )}
                            </label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <input
                                    style={{ ...styles.input, flex: 1 }}
                                    type="number"
                                    step="0.0001"
                                    min="0"
                                    placeholder="Kur giriniz..."
                                    value={exchangeRate}
                                    onChange={e => {
                                        setExchangeRate(Number(e.target.value) || 0);
                                        setRateSource('manual');
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={async () => {
                                        await fetchRates();
                                        if (tcmbRates && tcmbRates[currency]) {
                                            setExchangeRate(tcmbRates[currency]);
                                            setRateSource('tcmb');
                                        }
                                    }}
                                    disabled={loadingRates}
                                    style={{
                                        ...styles.secondaryButton,
                                        marginRight: 0,
                                        padding: '8px 12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        opacity: loadingRates ? 0.6 : 1
                                    }}
                                    title="TCMB'den güncel kuru çek"
                                >
                                    {loadingRates ? '⏳' : '🔄'} TCMB
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Döviz Bilgi Kutusu */}
                {currency !== 'TRY' && exchangeRate > 0 && (
                    <div style={styles.exchangeRateBox}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '24px' }}>💱</span>
                                <div>
                                    <div style={{ fontWeight: '600', color: '#92400e' }}>
                                        Döviz Kuru: 1 {currency} = {exchangeRate.toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} ₺
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#a16207' }}>
                                        Tüm fiyatlar {getCurrencySymbol(currency)} cinsinden girilecek ve TL'ye çevrilecektir.
                                    </div>
                                </div>
                            </div>
                            {rateSource === 'tcmb' && tcmbRates && (
                                <div style={styles.tcmbBadge}>
                                    🏛️ TCMB Güncel Kur ({tcmbRates.date})
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div style={{ marginBottom: '25px' }}>
                    <label style={styles.label}>Not</label>
                    <textarea
                        style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
                        placeholder="Satın alma ile ilgili notlar..."
                        value={note}
                        onChange={e => setNote(e.target.value)}
                    />
                </div>

                <div style={styles.itemInputRow}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Hammadde Seçimi</label>
                        <input
                            type="text"
                            placeholder="Hammadde adı yazın..."
                            style={styles.input}
                            value={currentItem.productName}
                            onChange={e => {
                                setCurrentItem(prev => ({ ...prev, productName: e.target.value, productId: '' }));
                                setShowMaterialSuggestions(true);
                            }}
                            onFocus={() => setShowMaterialSuggestions(true)}
                        />
                        {showMaterialSuggestions && filteredRawMaterials.length > 0 && !currentItem.productId && (
                            <div style={styles.suggestions}>
                                {filteredRawMaterials.map(p => (
                                    <div
                                        key={p.id}
                                        style={styles.suggestionItem}
                                        onMouseDown={() => handleRawMaterialSelect(p)}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                    >
                                        <div style={{ fontWeight: '500' }}>{p.name}</div>
                                        <div style={{ fontSize: '12px', color: '#94a3b8' }}>Stok: {p.quantity} {p.unit}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Miktar</label>
                        <input
                            style={styles.input}
                            type="number"
                            min="0"
                            placeholder="0"
                            value={currentItem.quantity}
                            onChange={e => setCurrentItem(prev => ({ ...prev, quantity: e.target.value }))}
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Birim Fiyat ({getCurrencySymbol(currency)})</label>
                        <input
                            style={styles.input}
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            value={currentItem.price}
                            onChange={e => setCurrentItem(prev => ({ ...prev, price: e.target.value }))}
                        />
                    </div>
                    <button onClick={handleAddItem} style={{ ...styles.button, height: '42px', marginTop: '0', display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                        + Ekle
                    </button>
                </div>

                {lineItems.length > 0 ? (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Hammadde</th>
                                <th style={styles.th}>Miktar</th>
                                <th style={styles.th}>Birim Fiyat ({getCurrencySymbol(currency)})</th>
                                <th style={styles.th}>Toplam ({getCurrencySymbol(currency)})</th>
                                {currency !== 'TRY' && <th style={styles.th}>TL Karşılığı</th>}
                                <th style={styles.th}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems.map((item, index) => (
                                <tr key={index}>
                                    <td style={styles.td}>{item.productName}</td>
                                    <td style={styles.td}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                                style={{ ...styles.input, width: '80px', padding: '6px' }}
                                                type="number"
                                                min="0"
                                                value={item.quantity}
                                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                            />
                                            <span>{item.unit}</span>
                                        </div>
                                    </td>
                                    <td style={styles.td}>
                                        <input
                                            style={{ ...styles.input, width: '100px', padding: '6px' }}
                                            type="number"
                                            value={item.price}
                                            onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                                        />
                                    </td>
                                    <td style={styles.td}><span style={{ fontWeight: '600' }}>{item.total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {getCurrencySymbol(currency)}</span></td>
                                    {currency !== 'TRY' && <td style={styles.td}><span style={{ fontWeight: '600', color: '#06C167' }}>{(item.total * exchangeRate).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span></td>}
                                    <td style={styles.td}>
                                        <button style={styles.deleteButton} onClick={() => handleRemoveItem(index)}>×</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px', border: '1px dashed #e2e8f0', borderRadius: '12px' }}>
                        Henüz hammadde eklenmedi.
                    </div>
                )}

                <div style={styles.totals}>
                    <div style={styles.totalBox}>
                        <div style={styles.totalRow}>
                            <span style={{ color: '#64748b' }}>Ara Toplam ({getCurrencySymbol(currency)}):</span>
                            <span>{total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {getCurrencySymbol(currency)}</span>
                        </div>
                        {currency !== 'TRY' && (
                            <div style={styles.totalRow}>
                                <span style={{ color: '#64748b' }}>Döviz Kuru:</span>
                                <span>1 {currency} = {exchangeRate.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
                            </div>
                        )}
                        <div style={styles.totalRow}>
                            <span style={{ color: '#64748b' }}>KDV (0%):</span>
                            <span>0.00 ₺</span>
                        </div>
                        <div style={{ ...styles.totalRow, ...styles.grandTotal }}>
                            <span>Genel Toplam (TL)</span>
                            <span style={{ color: '#4f46e5' }}>{totalTRY.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
                        </div>
                        {currency !== 'TRY' && (
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', textAlign: 'center' }}>
                                ({total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {getCurrencySymbol(currency)} × {exchangeRate} = {totalTRY.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺)
                            </div>
                        )}
                        <button onClick={handleSave} style={{ ...styles.button, width: '100%', marginTop: '20px', fontSize: '16px' }}>
                            Kaydet ve Bitir
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default NewRawMaterialPurchase;